import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
} from "react";
import "./BusinessCategoryCombobox.css";

interface BusinessCategoryComboboxProps {
  categories: readonly string[];
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  inputRef?: (element: HTMLInputElement | null) => void;
  id?: string;
  label?: string;
  placeholder?: string;
  help?: string;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  allowCustom?: boolean;
  searchable?: boolean;
  sortOptions?: boolean;
  listboxLabel?: string;
}

function normalizedCategories(categories: readonly string[], sortOptions: boolean): string[] {
  const unique = new Map<string, string>();
  categories.forEach((category) => {
    const value = String(category || "").trim();
    if (!value || value.toLowerCase() === "other / add new category") return;
    const key = value.toLocaleLowerCase();
    if (!unique.has(key)) unique.set(key, value);
  });
  const values = Array.from(unique.values());
  if (!sortOptions) return values;
  return values.sort((left, right) => left.localeCompare(right, undefined, {
    sensitivity: "base",
    numeric: true,
  }));
}

export function BusinessCategoryCombobox({
  categories,
  value,
  onChange,
  onBlur,
  inputRef,
  id,
  label = "Business category",
  placeholder = "Search business categories",
  help = "Choose a suggestion or add a new category.",
  error,
  disabled = false,
  required = false,
  className = "",
  allowCustom = true,
  searchable = true,
  sortOptions = true,
  listboxLabel = "Business category suggestions",
}: BusinessCategoryComboboxProps) {
  const generatedId = useId();
  const inputId = id || `business-category-${generatedId}`;
  const listboxId = `${inputId}-listbox`;
  const helpId = `${inputId}-help`;
  const errorId = `${inputId}-error`;
  const rootRef = useRef<HTMLDivElement>(null);
  const localInputRef = useRef<HTMLInputElement>(null);
  const suppressOpenOnFocusRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [customMode, setCustomMode] = useState(false);
  const sorted = useMemo(() => normalizedCategories(categories, sortOptions), [categories, sortOptions]);
  const selectedValue = value.trim().toLocaleLowerCase();
  const query = searchable ? selectedValue : "";
  const matches = useMemo(
    () => sorted.filter((category) => !query || category.toLocaleLowerCase().includes(query)),
    [query, sorted],
  );
  const exactMatch = sorted.some((category) => category.toLocaleLowerCase() === selectedValue);
  const safeActiveIndex = activeIndex >= matches.length
    ? matches.length - 1
    : activeIndex;
  const activeOptionId = safeActiveIndex >= 0 && matches[safeActiveIndex]
    ? `${inputId}-option-${safeActiveIndex}`
    : undefined;

  useEffect(() => {
    if (!open) return undefined;
    const closeOutside = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener("mousedown", closeOutside);
    return () => document.removeEventListener("mousedown", closeOutside);
  }, [open]);

  const focusInputWithoutOpening = () => requestAnimationFrame(() => {
    suppressOpenOnFocusRef.current = true;
    localInputRef.current?.focus();
    suppressOpenOnFocusRef.current = false;
  });
  const selectCategory = (category: string) => {
    onChange(category);
    setCustomMode(false);
    setOpen(false);
    setActiveIndex(-1);
  };
  const addCustomCategory = () => {
    const custom = value.trim();
    onChange(custom && !exactMatch ? custom : "");
    setCustomMode(true);
    setOpen(false);
    setActiveIndex(-1);
    focusInputWithoutOpening();
  };
  const openOptions = () => {
    if (disabled) return;
    setOpen(true);
    const selected = matches.findIndex((category) => category.toLocaleLowerCase() === selectedValue);
    setActiveIndex(selected >= 0 ? selected : matches.length ? 0 : -1);
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!open) {
        openOptions();
        return;
      }
      setActiveIndex(Math.min(matches.length - 1, safeActiveIndex + 1));
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) {
        openOptions();
        return;
      }
      setActiveIndex(safeActiveIndex < 0 ? Math.max(0, matches.length - 1) : Math.max(0, safeActiveIndex - 1));
      return;
    }
    if (event.key === "Enter" && open && safeActiveIndex >= 0 && matches[safeActiveIndex]) {
      event.preventDefault();
      selectCategory(matches[safeActiveIndex]);
      return;
    }
    if (event.key === "Escape" && open) {
      event.preventDefault();
      setOpen(false);
      setActiveIndex(-1);
    }
  };
  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (rootRef.current?.contains(event.relatedTarget as Node | null)) return;
    setOpen(false);
    setActiveIndex(-1);
    if (allowCustom && value.trim() && !exactMatch) setCustomMode(true);
    onBlur?.();
  };
  const customLabel = value.trim() && !exactMatch
    ? `Add “${value.trim()}” as a new category`
    : "Add new category";

  return <div className={`business-category-field ${className}`} ref={rootRef} onBlur={handleBlur}>
    <label htmlFor={inputId}>{label}</label>
    <div className="business-category-combobox">
      <input
        id={inputId}
        ref={(element) => {
          localInputRef.current = element;
          inputRef?.(element);
        }}
        role="combobox"
        aria-autocomplete={searchable ? "list" : "none"}
        aria-controls={listboxId}
        aria-expanded={open}
        aria-activedescendant={activeOptionId}
        aria-describedby={error ? errorId : helpId}
        aria-invalid={Boolean(error) || undefined}
        autoComplete="off"
        disabled={disabled}
        readOnly={!searchable}
        required={required}
        placeholder={customMode ? "Type your new business category" : placeholder}
        value={value}
        onFocus={() => {
          if (!suppressOpenOnFocusRef.current) openOptions();
        }}
        onClick={() => {
          if (!open) openOptions();
        }}
        onChange={(event) => {
          if (!searchable) return;
          onChange(event.target.value);
          setCustomMode(false);
          setOpen(true);
          setActiveIndex(0);
        }}
        onKeyDown={handleKeyDown}
      />
      <span className="business-category-chevron" aria-hidden="true" />
      {open ? <div className="business-category-menu">
        <ul id={listboxId} role="listbox" aria-label={listboxLabel}>
          {matches.map((category, index) => {
            const selected = category.toLocaleLowerCase() === selectedValue;
            const active = index === safeActiveIndex;
            return <li
              id={`${inputId}-option-${index}`}
              role="option"
              aria-selected={selected}
              className={`${selected ? "selected" : ""} ${active ? "active" : ""}`}
              key={category}
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => selectCategory(category)}
            >
              <span>{category}</span>
              {selected ? <strong aria-hidden="true">✓</strong> : null}
            </li>;
          })}
        </ul>
        {!matches.length ? <p className="business-category-empty" role="status">No matching categories</p> : null}
        {allowCustom ? <button
          className="business-category-add"
          type="button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={addCustomCategory}
        >
          <span aria-hidden="true">+</span>
          <strong>{customLabel}</strong>
        </button> : null}
      </div> : null}
    </div>
    {error
      ? <small className="business-category-error rt-field-error" id={errorId} role="alert">{error}</small>
      : <small className="business-category-help rt-field-help" id={helpId}>{customMode ? "This new category will be saved for this workspace." : help}</small>}
  </div>;
}
